/**
 * wineHelpers.js
 * Funções utilitárias para manipulação de dados de vinhos
 * Centraliza lógica comum para evitar duplicação de código
 */

/**
 * Constantes de classificação de vinhos
 */
export const CLASSIFICATION_TYPES = {
  TINTO: 'tinto',
  BRANCO: 'branco',
  ROSE: 'rosé',
  ROSE_ALT: 'rose' // Variação sem acento
};

/**
 * Retorna a classe CSS de cor baseada na classificação do vinho
 * @param {string} classification - Classificação do vinho (tinto, branco, rosé)
 * @returns {string} - Classes Tailwind CSS
 */
export const getClassificationColor = (classification) => {
  const colors = {
    [CLASSIFICATION_TYPES.TINTO]: 'bg-red-900 text-red-200',
    [CLASSIFICATION_TYPES.BRANCO]: 'bg-yellow-100 text-yellow-800',
    [CLASSIFICATION_TYPES.ROSE]: 'bg-pink-200 text-pink-800',
    [CLASSIFICATION_TYPES.ROSE_ALT]: 'bg-pink-200 text-pink-800'
  };

  return colors[classification] || 'bg-gray-700 text-gray-200';
};

/**
 * Formata preço para padrão brasileiro (R$ 99,99)
 * @param {number} price - Preço numérico
 * @returns {string} - Preço formatado
 */
export const formatPrice = (price) => {
  if (typeof price !== 'number' || isNaN(price)) {
    return 'R$ 0,00';
  }

  return `R$ ${price.toFixed(2).replace('.', ',')}`;
};

/**
 * Formata percentual de álcool
 * @param {string|number} alcohol - Teor alcoólico
 * @returns {string} - Álcool formatado
 */
export const formatAlcohol = (alcohol) => {
  if (!alcohol) {
    return '-';
  }

  // Se já tiver %, retorna como está
  if (typeof alcohol === 'string' && alcohol.includes('%')) {
    return alcohol;
  }

  // Se for número, adiciona %
  if (typeof alcohol === 'number') {
    return `${alcohol}%`;
  }

  return alcohol;
};

/**
 * Normaliza slug para URL-friendly
 * @param {string} text - Texto para converter em slug
 * @returns {string} - Slug normalizado
 */
export const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Decompor caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Remover acentos
    .replace(/[^\w\s-]/g, '') // Remover caracteres especiais
    .replace(/\s+/g, '-') // Substituir espaços por hífens
    .replace(/--+/g, '-') // Remover hífens duplicados
    .trim();
};

/**
 * Filtra vinhos por visibilidade
 * @param {Array} wines - Array de vinhos
 * @returns {Array} - Vinhos visíveis
 */
export const getVisibleWines = (wines) => {
  return wines.filter(wine => wine.visible !== false);
};

/**
 * Filtra vinhos por classificação
 * @param {Array} wines - Array de vinhos
 * @param {string} classification - Classificação desejada
 * @returns {Array} - Vinhos filtrados
 */
export const filterByClassification = (wines, classification) => {
  if (classification === 'todos' || !classification) {
    return wines;
  }

  return wines.filter(wine => wine.classification === classification);
};

export const filterByGrape = (wines, grape) => {
  if (grape === 'todas' || !grape) {
    return wines;
  }

  return wines.filter((wine) => wine.grape === grape);
};

export const applyWineListFilters = (wines, filters, priceRanges) => {
  let result = filterByClassification(wines, filters.classification);
  result = filterByGrape(result, filters.grape);

  if (filters.priceRangeId !== 'todos') {
    const range = priceRanges.find((r) => r.id === filters.priceRangeId);
    if (range) {
      result = filterByPriceRange(result, range.min, range.max);
    }
  }

  return sortWines(result, filters.sortOrder);
};

export const getWineListPageTitle = ({ selectedFood, selectedRegion, category, categories }) => {
  if (selectedFood) {
    return `Vinhos para ${selectedFood.name}`;
  }
  if (selectedRegion) {
    return `Vinhos de ${selectedRegion.name}`;
  }
  return (categories.find((c) => c.id === category) || categories[3]).subtitle;
};

/**
 * Filtra vinhos por faixa de preço
 * @param {Array} wines - Array de vinhos
 * @param {number} min - Preço mínimo
 * @param {number} max - Preço máximo
 * @returns {Array} - Vinhos filtrados
 */
export const filterByPriceRange = (wines, min, max = Infinity) => {
  return wines.filter(wine => wine.price >= min && wine.price < max);
};

/**
 * Ordena vinhos
 * @param {Array} wines - Array de vinhos
 * @param {string} sortBy - Critério de ordenação: 'nome', 'preco-asc', 'preco-desc'
 * @returns {Array} - Vinhos ordenados (cópia do array)
 */
export const sortWines = (wines, sortBy = 'nome') => {
  const winesCopy = [...wines];

  switch (sortBy) {
    case 'preco-asc':
      return winesCopy.sort((a, b) => a.price - b.price);

    case 'preco-desc':
      return winesCopy.sort((a, b) => b.price - a.price);

    case 'nome':
    default:
      return winesCopy.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }
};

/**
 * Obtém lista única de uvas disponíveis
 * @param {Array} wines - Array de vinhos
 * @returns {Array} - Array de uvas únicas, ordenadas
 */
export const getUniqueGrapes = (wines) => {
  const grapes = [...new Set(wines.map(wine => wine.grape))];
  return grapes.sort((a, b) => a.localeCompare(b, 'pt-BR'));
};

/**
 * Obtém lista única de regiões disponíveis
 * @param {Array} wines - Array de vinhos
 * @returns {Array} - Array de regiões únicas, ordenadas
 */
export const getUniqueRegions = (wines) => {
  const regions = [...new Set(wines.map(wine => wine.region))];
  return regions.sort((a, b) => a.localeCompare(b, 'pt-BR'));
};

/**
 * Busca vinho por slug
 * @param {Array} wines - Array de vinhos
 * @param {string} slug - Slug do vinho
 * @returns {Object|null} - Vinho encontrado ou null
 */
export const findWineBySlug = (wines, slug) => {
  return wines.find(wine => wine.slug === slug) || null;
};

/**
 * Busca vinho por ID
 * @param {Array} wines - Array de vinhos
 * @param {number} id - ID do vinho
 * @returns {Object|null} - Vinho encontrado ou null
 */
export const findWineById = (wines, id) => {
  return wines.find(wine => wine.id === id) || null;
};

/**
 * Calcula estatísticas de vinhos
 * @param {Array} wines - Array de vinhos
 * @returns {Object} - Estatísticas
 */
export const getWineStatistics = (wines) => {
  if (!wines || wines.length === 0) {
    return {
      total: 0,
      avgPrice: 0,
      minPrice: 0,
      maxPrice: 0,
      byClassification: {}
    };
  }

  const prices = wines.map(w => w.price);
  const avgPrice = prices.reduce((sum, p) => sum + p, 0) / prices.length;

  const byClassification = wines.reduce((acc, wine) => {
    acc[wine.classification] = (acc[wine.classification] || 0) + 1;
    return acc;
  }, {});

  return {
    total: wines.length,
    avgPrice: avgPrice,
    minPrice: Math.min(...prices),
    maxPrice: Math.max(...prices),
    byClassification
  };
};

/**
 * Verifica se uma string é um slug válido
 * @param {string} slug - String para validar
 * @returns {boolean} - True se válido
 */
export const isValidSlug = (slug) => {
  const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  return slugRegex.test(slug);
};

/**
 * Retorna label amigável para intensidade
 * @param {number} intensity - Intensidade (1-5)
 * @returns {string} - Label
 */
export const getIntensityLabel = (intensity) => {
  const labels = {
    1: 'Muito Leve',
    2: 'Leve',
    3: 'Médio',
    4: 'Forte',
    5: 'Intenso'
  };

  return labels[intensity] || 'Médio';
};

/**
 * Retorna label amigável para tanino
 * @param {number} tannin - Tanino (1-5)
 * @returns {string} - Label
 */
export const getTanninLabel = (tannin) => {
  const labels = {
    1: 'Muito Macio',
    2: 'Macio',
    3: 'Equilibrado',
    4: 'Presente',
    5: 'Austero'
  };

  return labels[tannin] || 'Equilibrado';
};
